/// <amd-module name="Environments/EnvironmentDetails/ActionsCreators/EnvironmentActionsCreator" />
import { ActionCreatorBase } from "Environments/Flux/ActionCreatorBase";
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { EnvironmentActions } from "Environments/EnvironmentDetails/Actions/EnvironmentActions";
import { EnvironmentTabKeys } from "Environments/EnvironmentsCommon/Utils";
import { getEnvironmentsPageUrl, getEnvironmentPageUrl } from "Environments/EnvironmentsCommon/Link";
import { MessagesActions } from "Environments/EnvironmentsCommon/MessagesActions";
import { IEnvironmentService } from "Environments/Client/EnvironmentService";
import * as Contracts from "DistributedTask/Client/Contracts/TaskAgent";
import { IVssPageContext } from "VSS/Platform/Context";
import { PageKey, getErrorMessage } from "Environments/EnvironmentDetails/Common/Utils";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { EnvironmentJobStatus, IEnvironmentResourceObject, IEnvironmentObject, IEnvironmentResult } from "Environments/Client/EnvironmentContracts";
import { convertIEnvironmentObjectFromEnvironmentInstance } from "Environments/EnvironmentDetails/Common/Utils";
import { FastPageSwitch } from "VSS/Platform/FPS";

export class EnvironmentActionsCreator extends ActionCreatorBase {

    public static getKey(): string {
        return "environment-action-creator";
    }

    public initialize(instanceId?: string) {
        super.initialize(instanceId);
        
        this._actions = ActionsHubManager.CreateActionsHub<EnvironmentActions>(EnvironmentActions, instanceId);
        this._messageActions = ActionsHubManager.CreateActionsHub<MessagesActions>(MessagesActions, instanceId);
    }

    public updateEnvironment(environment: Contracts.EnvironmentInstance): void {
        this._actions.updateEnvironment.invoke(convertIEnvironmentObjectFromEnvironmentInstance(environment));
    }

    // Returns true if environment information is found in pageData and call returns synchronously.
    // Returns false if explicit API call is being made to fetch the data asynchronously.
    public getEnvironmentById(pageContext: IVssPageContext, environmentId: number, includeSGReferences: boolean = true, forceRefresh: boolean = false): boolean {
        const environmentService: IEnvironmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        const environment = environmentService.getEnvironmentFromPageData();
        
        if (!!environment && !forceRefresh) {
            this._actions.setEnvironment.invoke(environment);
            return true;
        }
        
        const expands: Contracts.EnvironmentExpands = includeSGReferences ? Contracts.EnvironmentExpands.ResourceReferences : Contracts.EnvironmentExpands.None;
        environmentService.getEnvironmentById(environmentId, expands).then((environment: Contracts.EnvironmentInstance) => {
            this._actions.setEnvironment.invoke(convertIEnvironmentObjectFromEnvironmentInstance(environment));
        },
        (error) => {
            this._messageActions.addMessage.invoke({ key: PageKey.EnvironmentView, value: getErrorMessage(error) });
        });

        return false;
    }

    public deleteEnvironment(pageContext: IVssPageContext, id: number): void {
        const environmentService = pageContext.getService<IEnvironmentService>("IEnvironmentService");
        environmentService.deleteEnvironment(id).then(() => {
            FastPageSwitch(pageContext, getEnvironmentsPageUrl(pageContext), true);
        },
        (error) => {
            this._messageActions.addMessage.invoke({ key: PageKey.EnvironmentView, value: getErrorMessage(error) });
        });
    }

    public deleteResource(pageContext: IVssPageContext, environmentId: number, resourceId: number, resourceType: Contracts.EnvironmentResourceType): void {
        pageContext.getService<IEnvironmentService>("IEnvironmentService").deleteResource(environmentId, resourceId, resourceType).then(() => {
            this._actions.deleteResource.invoke(resourceId);
        },
        (error) => {
            this._messageActions.addMessage.invoke({ key: PageKey.EnvironmentView, value: getErrorMessage(error) });
        });
    }

    public getResourcesWithFilters(
        pageContext: IVssPageContext, 
        resourceName?: string, 
        environmentResourceType: Contracts.EnvironmentResourceType = Contracts.EnvironmentResourceType.Generic | Contracts.EnvironmentResourceType.Kubernetes | Contracts.EnvironmentResourceType.VirtualMachine | Contracts.EnvironmentResourceType.Undefined, 
        environmentJobStatus: EnvironmentJobStatus = EnvironmentJobStatus.None, 
        includeLastCompletedRequest: boolean = true,
        includeProviderSpecificData: boolean = false,
        forceRefresh: boolean = false): void {

            const inputProperties = {
                includeLastCompletedRequest: includeLastCompletedRequest,
                resourceName: resourceName,
                environmentJobStatus: environmentJobStatus,
                environmentResourceType: environmentResourceType,
                includeProviderSpecificData: includeProviderSpecificData
            };

            const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
            const promise = contributionService.getDataAsync<IEnvironmentResult>("ms.vss-environments-web.environment-resources-view-data-provider", inputProperties, forceRefresh);

            promise.then((environmentResult: IEnvironmentResult) => {
                const resources: IEnvironmentResourceObject[] = environmentResult.environment.resources;
                this._actions.updateResources.invoke(resources);
            },
            (error) => {
                this._messageActions.addMessage.invoke({ key: PageKey.EnvironmentView, value: getErrorMessage(error) });
            });
    }

    public updateEnvironmentResourceTags(pageContext: IVssPageContext, resourceId: number, resourceType: Contracts.EnvironmentResourceType, newTagsSet: string[]): Promise<Contracts.EnvironmentResource> {
        const promise = pageContext.getService<IEnvironmentService>("IEnvironmentService").updateEnvironmentResourceTags(resourceId, resourceType, newTagsSet);
        return promise;
    }

    public deleteEnvironmentResource(pageContext: IVssPageContext, environmentId: number, resourceId: number, resourceType: Contracts.EnvironmentResourceType): Promise<void> {
        const promise = pageContext
            .getService<IEnvironmentService>("IEnvironmentService")
            .deleteEnvironmentResource(resourceId, resourceType);
        
            promise.then(() => {
                FastPageSwitch(pageContext, getEnvironmentPageUrl(pageContext, environmentId, EnvironmentTabKeys.EnvironmentResourcesTabKey), true);
            },
            (error) => {
                this._messageActions.addMessage.invoke({ key: PageKey.EnvironmentView, value: getErrorMessage(error) });
            });

        return promise;
    }

    protected disposeInternal(): void {
        ActionsHubManager.DeleteActionsHub<EnvironmentActions>(EnvironmentActions, this.getInstanceId());
        ActionsHubManager.DeleteActionsHub<MessagesActions>(MessagesActions, this.getInstanceId());
    }

    private _actions: EnvironmentActions;
    private _messageActions: MessagesActions;
}